<script setup>
  import Button from "@/base-components/Button";
  import Lucide from "@/base-components/Lucide";
  import { useFetch } from "@/composables/useFetch.js";
  import { onMounted, ref } from "vue";
  import Vue3Datatable from "@bhplugin/vue3-datatable";
  import "@bhplugin/vue3-datatable/dist/style.css";
  import { useRoute, useRouter } from "vue-router";
  import { uri } from "@/constants/config.js";
  import DepartmentDialog from "./DepartmentDialog.vue";
  import "vue-awesome-paginate/dist/style.css";
  import BasePagination from "@/base-components/Table/BasePagination.vue";
  import { useI18n } from "vue-i18n";
  import moment from "moment-jalaali";

  const { role } = defineProps({
    role: {
      type: String,
      default: "admin",
    },
  });

  const { t: $t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const isLoadingFlag = ref(true);
  const departmentList = ref([]);
  const cols = [
    { field: "counter", title: "#" },
    { field: "id", title: $t("title.id") },
    { field: "title", title: $t("title.title") },
  ];
  const pageOptions = ref({
    count: 1,
    limit: 10,
    page: 1,
  });

  function onSuccessOpenDepartment() {
    setOpenDepartmentFlag(false);
    fetchData();
  }

  const getDepartmentList = async () => {
    isLoadingFlag.value = true;
    let response = null;
    const urls = {
      admin: `${uri.admin.support.department.list}?page=${pageOptions.value.page}`,
      cloud: `${uri.admin.cloud.support.department.list}?page=${pageOptions.value.page}`,
    };
    const url = urls[role];

    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        response = data;
        pageOptions.value.count = data.meta.total;
        pageOptions.value.limit = data.meta.perPage;
      }
    } catch (err) {
      console.log(err);
    }
    return response;
  };

  const fetchData = async () => {
    try {
      const [department] = await Promise.all([getDepartmentList()]);
      departmentList.value = department.data;
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      isLoadingFlag.value = false;
    }
  };

  const openDepartmentFlag = ref(false);

  function setOpenDepartmentFlag(value) {
    openDepartmentFlag.value = value;
  }

  function openDepartmentDialog() {
    openDepartmentFlag.value = true;
  }

  function rowClick(department) {}

  // Fetch data when the component mounts
  onMounted(fetchData);
</script>
<template>
  <section>
    <DepartmentDialog
      v-if="openDepartmentFlag"
      @close="setOpenDepartmentFlag(false)"
      @onSuccess="onSuccessOpenDepartment"
      :show="openDepartmentFlag"
    />

    <div class="grid grid-cols-12 gap-6 mt-8">
      <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
        <!-- BEGIN: Inbox Content -->
        <div class="mt-5 intro-y box">
          <div
            class="flex p-5 border-b sm:flex-row text-slate-500 border-slate-200/60 text-end w-full justify-end items-end"
          >
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="fetchData">
              <Lucide icon="RefreshCw" class="w-6 h-6" />
            </div>
            <div
              class="w-5 h-5 ms-5 cursor-pointer"
              @click="openDepartmentDialog"
            >
              <Lucide icon="PlusCircle" class="w-6 h-6" />
            </div>
          </div>
          <div class="mb-5 relative"></div>

          <vue3-datatable
            :columns="cols"
            :rows="departmentList"
            :loading="isLoadingFlag"
            :totalRows="pageOptions.count"
            :isServerMode="true"
            :pageSize="pageOptions.limit"
            :pagination="false"
            :sortable="false"
            class="advanced-table px-4 text-start items-start justify-start"
            rowClass="cursor-pointer"
            @rowClick="rowClick"
          >
            <!-- Empty header slot to remove the header -->
            <template #header>
              <!-- No content here will hide the header -->
            </template>
            <!-- Empty footer slot to remove the footer -->
            <template #footer>
              <!-- No content here will hide the footer -->
            </template>

            <template #counter="data, key">
              <div class="text-start">
                {{
                  departmentList.indexOf(data.value) +
                  1 +
                  (pageOptions.page - 1) * pageOptions.limit
                }}
              </div>
            </template>

            <template #department="data, key">
              <div class="text-start">
                {{
                  departmentList.find(
                    (item) => item.id === data.value.departmentId
                  )?.name ?? "-"
                }}
              </div>
            </template>

            <template #id="data, key">
              <div class="text-start">
                {{ data.value.id }}
              </div>
            </template>

            <template #title="data, key">
              <div class="text-start">
                {{ data.value.name }}
              </div>
            </template>

            <template #createdAt="data, key">
              <div class="text-start">
                {{
                  data.value.createdAt
                    ? moment(data.value.createdAt)
                        .locale("fa")
                        .format("jYYYY/jMM/jDD HH:mm")
                    : "-"
                }}
              </div>
            </template>
            <template #updatedAt="data, key">
              <div class="text-start">
                {{
                  data.value.updatedAt
                    ? moment(data.value.updatedAt)
                        .locale("fa")
                        .format("jYYYY/jMM/jDD HH:mm")
                    : "-"
                }}
              </div>
            </template>
            <template #closedAt="data, key">
              <div class="text-start">
                {{
                  data.value.closedAt
                    ? moment(data.value.closedAt)
                        .locale("fa")
                        .format("jYYYY/jMM/jDD HH:mm")
                    : "-"
                }}
              </div>
            </template>

            <template #status="data, key">
              <div class="text-start">
                {{ data.value.isOpen ? $t("title.open") : $t("title.close") }}
              </div>
            </template>
          </vue3-datatable>
          <div
            class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
          >
            <div>
              <BasePagination
                v-if="pageOptions.count > pageOptions.limit"
                :page-options="pageOptions"
                @page-change="fetchData"
              />
            </div>
            <div class="mt-2 sm:ml-auto sm:mt-0"></div>
          </div>
        </div>
        <!-- END: Inbox Content -->
      </div>
    </div>
  </section>
</template>
